"use client";

import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { FlexColumnAlignJustifyCenter, FlexRowAlignCenter } from "@/components/Flex";
import LinkWrapper from "@/components/wrappers/Link/Link";
import { ASSETS_URL } from "@/config/deployConstants";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import Image from "next/image";
import { usePathname } from "next/navigation";

// Utility function to convert a wildcard pattern to a RegExp
const wildcardToRegExp = (pattern: string): RegExp => {
    // Escape regex special characters except for "*"
    const escaped = pattern.replace(/[-\/\\^$+?.()|[\]{}]/g, "\\$&");
    // Replace "*" with a regex pattern that matches one or more non-slash characters
    const regexPattern = "^" + escaped.replace(/\*/g, "[^/]+") + "$";
    return new RegExp(regexPattern);
};

const isRouteMatch = (pathname: string, routePattern: string): boolean => {
    // If the route contains a wildcard "*", use regex matching.
    // Otherwise, fall back to a simple endsWith.
    if (routePattern.includes("*")) {
        const regex = wildcardToRegExp(routePattern);

        return regex.test(pathname);
    } else {
        return pathname.endsWith(routePattern);
    }
};

const LandingPagesSwitcher = ({
    pages,
    containerStyle,
    hideKaiImage = false,
}: {
    pages: {
        name: string;
        route: string;
        alternateRoutes?: string[];
    }[];
    containerStyle?: React.CSSProperties;
    hideKaiImage?: boolean;
}) => {
    const pathname = usePathname();

    return (
        <FlexColumnAlignJustifyCenter
            style={{
                padding: `${spacing.SM} 0 0`,
                backgroundColor: "transparent",
                position: "relative",
                ...containerStyle,
            }}>
            {!hideKaiImage && (
                <Image
                    style={{ zIndex: 2, marginBottom: "-1.3rem" }}
                    src={`${ASSETS_URL}/images/kai-holding-button.svg`}
                    alt="Kai holding button"
                    width={132}
                    height={92}
                />
            )}
            <FlexRowAlignCenter
                style={{
                    height: "6rem",
                    backgroundColor: themeColors.flashcardsLight,
                    padding: "0 0.4rem",
                    borderRadius: "4rem",
                }}>
                {pages.map(page => {
                    const isExactPage = pathname.includes(page.route);
                    const isCurrentPage =
                        isExactPage || page.alternateRoutes?.some(route => isRouteMatch(pathname, route));

                    return (
                        <LinkWrapper key={page.name} href={page.route}>
                            <CircularRectTextButton
                                component={"div"}
                                className="secondaryTextBold1"
                                disabled={isExactPage}
                                style={{
                                    backgroundColor: isCurrentPage ? themeColors.primary3 : "transparent",
                                    color: isCurrentPage ? themeColors.pureBlack : themeColors.neutralBlack,
                                    height: "5.2rem",
                                    padding: `${spacing.XS_2} ${spacing.MD}`,
                                }}>
                                {page.name}
                            </CircularRectTextButton>
                        </LinkWrapper>
                    );
                })}
            </FlexRowAlignCenter>
        </FlexColumnAlignJustifyCenter>
    );
};

export default LandingPagesSwitcher;
